import { EditorRoleEnum } from './EditorRole'
import { z } from 'zod'
import { withFieldValue } from './FirestoreFieldValue'
import { DIFF_AS_ONE, DIFF_IGNORE } from '../diffConstants'
import { TemporaryAbsenceCompensationReasonEnum } from './Contract'

export const CompanyStats = z
  .object({
    activeContractCount: z.number(),
  })
  .strict()

export type CompanyStats = z.infer<typeof CompanyStats>

export enum PayrollMonth {
  AccountingMonth = 'AccountingMonth',
  FollowingMonth = 'FollowingMonth',
}

export const PayrollMonthEnum = z.nativeEnum(PayrollMonth)

// A value outside the enum is worse than none: getNextSettlementMonth matches no
// case and silently falls through to the current month. CL-2116
export function normalizePayrollMonth(value: unknown): PayrollMonth | null {
  const parsed = PayrollMonthEnum.safeParse(value)
  return parsed.success ? parsed.data : null
}

// Only touches the field when the caller actually sent it — on a partial update
// an absent key means "unchanged", so coercing it would wipe a stored setting.
export function withNormalizedPayrollMonth<
  T extends { payrollMonth?: unknown },
>(patch: T): T {
  if (!('payrollMonth' in patch)) {
    return patch
  }
  return { ...patch, payrollMonth: normalizePayrollMonth(patch.payrollMonth) }
}

export const CompanyBilling = z
  .object({
    stripeCustomerId: z.string().nullable(),
    payslipPrice: z.number(),
    trialSettlementPrice: z.number(),
    additionalHoursPrice: z.number(),
    email: z.string().trim().nullable(),
    minimumPayslips: z.number(),
    billTrialSettlements: z.boolean().default(false),
    daysUntilDue: z.number().int().min(0).nullable().default(null),
    // CL-1305: set on SEPA failure to route billing to `send_invoice` until
    // the customer re-signs. Auto-cleared by the next billing run.
    sepaNeedsReauth: z.boolean().default(false),
    // CL-2604: the client is billed through another company (e.g. insolvency),
    // so a month may be closed with 0 billed payslips despite active contracts.
    allowZeroPayrollClose: z.boolean().default(false),
  })
  .strict()

export type CompanyBilling = z.infer<typeof CompanyBilling>

export const u1LevyTypes = [
  'standard',
  'reduced',
  'increased',
  'mixed',
  'none',
] as const

export const U1LevyTypeEnum = z.enum(u1LevyTypes)

export type U1LevyType = (typeof u1LevyTypes)[number]

export const CompanyAccounting = z
  .object({
    registryNumber: z.string().trim().length(8).nullable(),
    chartOfAccounts: z.string().nullable(),
    incomeRegistrationPeriod: z.string().nullable(),
    profitDeterminationType: z.string().nullable(),
    immediateReportingObligation: z.boolean().nullable(),
    estimator: z.boolean().nullable(),
    companyType: z.string().nullable(),
    taxId: z.string().trim().min(10).max(13).nullable(),
    taxOfficeNumber: z
      .string()
      .trim()
      .regex(/^\d{4}$/)
      .nullable()
      .default(null),
    u1Levy: U1LevyTypeEnum.nullable().optional(),
  })
  .strict()

export type CompanyAccounting = z.infer<typeof CompanyAccounting>

export const CompanyBookingList = z
  .object({
    sendBookingList: z.boolean().nullable(),
    bookingListEmail: z.string().nullable(),
    bookingListPassword: z.string().nullable(),
  })
  .describe(DIFF_IGNORE)
  .strict()

export type CompanyBookingList = z.infer<typeof CompanyBookingList>

export const CompanyContact = z
  .object({
    name: z.string().trim().nullable(),
    phone: z.string().trim().nullable(),
    email: z.string().trim().email().nullable().default(null),
    additionalEmails: z.array(z.string().trim().email()).default([]),
  })
  .strict()
  .describe(DIFF_AS_ONE)

export type CompanyContact = z.infer<typeof CompanyContact>

export const federalStateValues = [
  'BW',
  'BY',
  'BE',
  'BB',
  'HB',
  'HH',
  'HE',
  'MV',
  'NI',
  'NW',
  'RP',
  'SL',
  'SN',
  'ST',
  'SH',
  'TH',
] as const

export const FederalStateEnum = z.enum(federalStateValues)

export type FederalState = (typeof federalStateValues)[number]

export const CompanyAddress = z
  .object({
    street: z.string().trim(),
    streetNumber: z.string().trim(),
    streetAdditional: z.string().trim().nullable(),
    city: z.string().trim(),
    zipCode: z.string().trim().length(5),
    country: z.string().trim().toLowerCase().length(2),
    federalState: FederalStateEnum.nullable().default(null),
  })
  .strict()
  .describe(DIFF_AS_ONE)

export type CompanyAddress = z.infer<typeof CompanyAddress>

export const CompanyBanking = z
  .object({
    accountOwner: z.string().trim(),
    bank: z.string().trim().nullable(),
    iban: z.string().trim(),
    bic: z.string().trim().nullable(),
  })
  .strict()

export type CompanyBanking = z.infer<typeof CompanyBanking>

export const CompanyDatev = z
  .object({
    // FiBu export to DATEV, not the payroll software. For payroll-automation
    // DATEV eligibility use `company.software` via isDatevPayrollSoftware().
    using: z.boolean(),
    userId: z.string().trim().nullable(),
    consultantId: z.string().trim().nullable(),
    // The Lohn (payroll) pair, when it differs from the FiBu pair above.
    //
    // DATEV numbers a client per Bereich: the Beraternummer/Mandantennummer a
    // Kanzlei runs the bookkeeping under need not be the one it runs the
    // payroll under, and for several of our clients they are not. `userId` and
    // `consultantId` above are the FiBu pair — that is what the Buchungsstapel
    // export and the Pulse FiBu path need. Everything on a payroll path (the
    // numbers printed on a payslip PDF, the LODAS file header) needs these.
    //
    // Absent or null means "the payroll numbers are the FiBu ones", which is
    // the case for every client that predates this field. Read through
    // `resolvePayrollConsultantNumber` / `resolvePayrollMandateNumber`, never
    // directly, so that fallback is never skipped.
    payrollConsultantId: z.string().trim().nullable().optional(),
    payrollUserId: z.string().trim().nullable().optional(),
    // Lohnarten the LODAS export books remuneration under. They come from the
    // client's own Lohnartenliste (`Eigene Lohnartennummer`), not from a DATEV
    // catalogue, so they differ per Mandant and cannot be derived. Absent or
    // null means "not known for this client": the salary then falls back to the
    // number the export has always used.
    salaryLohnart: z.number().int().positive().nullable().optional(),
    // Read by nothing. An hourly rate is not booked against a wage type at
    // all: it goes to `std_lohn_1#psd` in `u_lod_psd_lohn_gehalt_bezuege` as a
    // plain amount. Kept because the schema is strict and companies already
    // carry the field, which would then fail to load.
    hourlyLohnart: z.number().int().positive().nullable().optional(),
    // Lohnarten the movement-data export books absences under: continued pay
    // for holiday and for sickness. Same per-Mandant numbering as above, so
    // null suppresses the booking rather than guessing a number.
    vacationLohnart: z.number().int().positive().nullable().optional(),
    sicknessLohnart: z.number().int().positive().nullable().optional(),
    // Default Lohnart for imported Provisionen, which arrive without one. A
    // payment that carries its own `customTypeId` uses that instead.
    // Superseded by `paymentLohnarten.Commission`, which is read first; kept
    // because clients already carry it and the schema is strict.
    commissionLohnart: z.number().int().positive().nullable().optional(),
    // Lohnarten one-time payments book against, as a list rather than one
    // number per payment type.
    //
    // A client's catalogue names far more kinds of pay than our twelve payment
    // types do: one Mandant carries eight separate commission Lohnarten
    // (Provision KD, Provision EG, Zielerreichungsprovision and so on), and a
    // map keyed by payment type could hold exactly one of them. Everything
    // else in their file was then refused on import as an unknown wage type.
    //
    // So several Lohnarten may name the same payment type. Order is
    // precedence: the export writes the first entry given for a type, and the
    // import accepts every one of them. A number appearing twice under
    // different types is refused rather than guessed at, in
    // `invertPaymentLohnarten`.
    //
    // The numbers are the Mandant's own — 241 and 242 name Weihnachts- and
    // Urlaubsgeld in one client's list and something else entirely in the next
    // — so an unconfigured type is still left out rather than guessed at.
    paymentLohnarten: z
      .array(
        z
          .object({
            lohnart: z.number().int().positive(),
            // Validated against the payment types in `resolveLodasLohnarten`
            // and `invertPaymentLohnarten`. Typed as a plain string here
            // because `src/lib/shared` is a symlink into this tree: importing
            // the enum makes the model resolve through two module trees with
            // their own zod, and the two inferred types stop being assignable.
            type: z.string(),
          })
          .strict()
      )
      .nullable()
      .optional(),
    // The client's own Krankenkassen numbers, keyed by our insurer document
    // uid (`configs/healthInsurances/public`). DATEV identifies an insurer by
    // a three-digit number the client assigns, exactly as it does wage types,
    // so this cannot be derived from the Betriebsnummer we hold. Absent means
    // the export leaves the insurer field unset rather than guessing a number
    // that names somebody else's Kasse.
    // The value is `.optional()` so the inferred type carries `| undefined`,
    // matching what a partial company update produces when it is spread over a
    // stored one. `resolveLodasKrankenkassen` validates every entry anyway, so
    // nothing reaches the file on the strength of this type alone.
    krankenkassen: z
      .record(z.string(), z.number().int().min(1).max(999).optional())
      .nullable()
      .optional(),
    // Tätigkeitsschlüssel for designations the Bundesagentur's register does
    // not list, keyed by the designation as the client writes it. Their own
    // payroll data carries Berufsgattung titles and spellings the register has
    // no entry for — "Bürokraft/Kaufmännische Fachkraft" covers 178 of one
    // client's employees and appears nowhere in it — so one entry here answers
    // for everybody who carries that designation, instead of the code being
    // set on each contract in turn.
    activityKeys: z
      .record(
        z.string(),
        z
          .string()
          .regex(/^\d{5}$/)
          .optional()
      )
      .nullable()
      .optional(),
  })
  .strict()

export type CompanyDatev = z.infer<typeof CompanyDatev>

/**
 * The well-formed entries of a submitted Lohnarten list.
 *
 * A company update arrives as a deep partial, so a row the operator started
 * and did not finish reaches us with one half missing. Such a row is dropped
 * rather than stored: an entry naming a number but no payment type, or a type
 * but no number, maps nothing and would sit in the list looking as though it
 * did.
 *
 * A Lohnart that is not a positive whole number goes the same way, matching
 * what the schema accepts. `toFirestore` swallows a failed parse and writes the
 * data anyway, so this is the check that actually keeps a zero out.
 */
/**
 * A stored Lohnarten list, whatever shape it is stored in.
 *
 * The setting began as one number per payment type — `{ Commission: 29 }` —
 * and became a list so that several Lohnarten could name one type. Firestore
 * reads are not validated against this schema (the converter casts rather than
 * parses), so a document written under the old shape reaches a reader exactly
 * as it was stored, and iterating it as a list would throw.
 *
 * No company in production, staging or the AFH project carries the old shape,
 * so this is not a migration. It is here because a hand-written stopgap could
 * still put one there, and an export that throws is a bad way to find out.
 */
export const coercePaymentLohnarten = (
  value: unknown
): { lohnart: number; type: string }[] => {
  if (value == null) {
    return []
  }
  if (Array.isArray(value)) {
    return normalizePaymentLohnarten(value)
  }
  if (typeof value !== 'object') {
    return []
  }
  // The old shape: payment type -> number.
  return normalizePaymentLohnarten(
    Object.entries(value as Record<string, unknown>).map(([type, lohnart]) => ({
      lohnart: typeof lohnart === 'number' ? lohnart : undefined,
      type,
    }))
  )
}

export const normalizePaymentLohnarten = (
  entries:
    | readonly ({ lohnart?: number; type?: string } | undefined)[]
    | null
    | undefined
): { lohnart: number; type: string }[] =>
  (entries ?? []).flatMap((entry) =>
    typeof entry?.lohnart === 'number' &&
    Number.isInteger(entry.lohnart) &&
    entry.lohnart > 0 &&
    typeof entry?.type === 'string' &&
    entry.type !== ''
      ? [{ lohnart: entry.lohnart, type: entry.type }]
      : []
  )

// Agenda's DATEV Beraternummer (consultantId) and Mandantennummer (userId) are
// numeric identifiers. A non-numeric value — e.g. placeholder asterisks an
// employer typed to satisfy the required field — is invalid and breaks the
// downstream FiBu export (Pulse types it into a numeric Agenda field that
// rejects it). Enforced on write; not on the read converter, so legacy bad
// values still load and can be corrected.
export const DATEV_NUMBER_PATTERN = /^\d+$/

export function isValidDatevNumber(value: string | null | undefined): boolean {
  return DATEV_NUMBER_PATTERN.test((value ?? '').trim())
}

// Whether two datev objects hold the same values. Used to validate DATEV
// numbers only when an update actually changes them: the accounting form
// always submits the full datev object, so legacy records carrying invalid
// values (e.g. using=true with null numbers) must not fail validation when a
// request echoes the stored datev back unchanged (CL-1889 / CL-WEBAPP-P0).
// Compares the numbers only: the Lohnarten are unrelated to them and must not
// pull an unchanged Berater/Mandantennummer into validation.
export function datevEquals(
  a: CompanyDatev | null | undefined,
  b: CompanyDatev | null | undefined
): boolean {
  return (
    (a?.using ?? null) === (b?.using ?? null) &&
    (a?.userId ?? null) === (b?.userId ?? null) &&
    (a?.consultantId ?? null) === (b?.consultantId ?? null)
  )
}

/**
 * The Beraternummer payroll runs under, falling back to the FiBu one.
 *
 * DATEV can file a client's payroll under a different Berater/Mandant pair
 * than its bookkeeping. Only the payroll pair matches what a payslip PDF
 * prints and what a LODAS header must carry, so every payroll path resolves
 * through here.
 *
 * The payroll field wins only when it holds a usable number: a placeholder
 * (`***`, `n/a`) typed into it must not shadow a good FiBu number, or the
 * upload's foreign-number guard would see "nothing configured" and disable
 * itself — and Personalnummern repeat between Mandanten, so another client's
 * payslips would then be filed onto this company's employees.
 *
 * Returns null only when neither number is usable, which is what every caller
 * already handled before this field existed.
 */
export function resolvePayrollConsultantNumber(
  datev: CompanyDatev | null | undefined
): string | null {
  return resolvePayrollNumber(datev?.payrollConsultantId, datev?.consultantId)
}

/**
 * The Mandantennummer payroll runs under, falling back to the FiBu one.
 *
 * Same contract as `resolvePayrollConsultantNumber`.
 */
export function resolvePayrollMandateNumber(
  datev: CompanyDatev | null | undefined
): string | null {
  return resolvePayrollNumber(datev?.payrollUserId, datev?.userId)
}

/**
 * Whether payroll runs under the bookkeeping numbers.
 *
 * True when no Lohn number is stored — the state every client that predates
 * the field is in, and the state a save under one shared pair leaves behind —
 * or when a stored Lohn number only repeats its FiBu counterpart. False as
 * soon as either number is filed separately, which is what the editor and the
 * Ops card ask about before they decide to show one pair or two.
 */
export function payrollNumbersMatchBookkeeping(
  datev: CompanyDatev | null | undefined
): boolean {
  return (
    !isSeparatePayrollNumber(datev?.payrollConsultantId, datev?.consultantId) &&
    !isSeparatePayrollNumber(datev?.payrollUserId, datev?.userId)
  )
}

function isSeparatePayrollNumber(
  payrollValue: string | null | undefined,
  fibuValue: string | null | undefined
): boolean {
  const payroll = payrollValue?.trim() ?? ''
  return payroll !== '' && payroll !== (fibuValue?.trim() ?? '')
}

function resolvePayrollNumber(
  payrollValue: string | null | undefined,
  fibuValue: string | null | undefined
): string | null {
  const payroll = payrollValue?.trim() ?? ''
  if (isValidDatevNumber(payroll)) return payroll

  const fibu = fibuValue?.trim() ?? ''
  return fibu === '' ? null : fibu
}

export const CompanyTradeAssociation = z
  .object({
    name: z.string(),
    number: z.string().nullable(),
    pin: z.string().nullable(),
    hazardTariffStelle: z.string().nullable().default(null),
  })
  .strict()

export type CompanyTradeAssociation = z.infer<typeof CompanyTradeAssociation>

export const CompanyOperator = z
  .object({
    userUid: z.string(),
    displayName: z.string(),
    email: z.string(),
  })
  .strict()
  .describe(DIFF_AS_ONE)

export type CompanyOperator = z.infer<typeof CompanyOperator>

// export const EmployerInvitation = z
//   .object({
//     uid: z.string(),
//     createdAt: z.number(),
//     accepted: z.boolean(),
//     acceptedBy: z.string().nullable(),
//     acceptedAt: z.number().nullable(),
//   })
//   .strict()

// export type EmployerInvitation = z.infer<typeof EmployerInvitation>

export enum MandateType {
  DirectMandate = 'DirectMandate',
  Subcontract = 'Subcontract',
}

export const MandateTypeEnum = z.nativeEnum(MandateType)

// How the payroll agent provisions salary payments. With 'online_banking' the
// agent provides payments online instead of generating a SEPA file. Null
// resolves to the SEPA-file default.
export const paymentProvisioningValues = [
  'sepa_file',
  'online_banking',
] as const

export const PaymentProvisioningEnum = z.enum(paymentProvisioningValues)

export type PaymentProvisioning = (typeof paymentProvisioningValues)[number]

export const offboardingReasonValues = [
  'brought_in_house',
  'communication_problems',
  'consolidation_payroll_accounting',
  'insolvency',
  'insufficient_features',
  'no_employment',
  'other',
] as const

export const OffboardingReasonEnum = z.enum(offboardingReasonValues)

export type OffboardingReason = (typeof offboardingReasonValues)[number]

export const Company = z
  .object({
    active: z.boolean(),
    archived: z.boolean(),
    completed: z.boolean(),
    deleted: z.boolean(),
    demo: z.boolean(),
    software: z.string().nullable(),
    payrollDay: z.number().nullable(),
    payrollMonth: z.string().nullable(),
    payrollDate: z.string().date().nullable().describe(DIFF_IGNORE),
    lastSettlementMonth: z.string().nullable().describe(DIFF_IGNORE),
    lastSettlementAt: z
      .number()
      .nullable()
      .default(null)
      .catch(null)
      .describe(DIFF_IGNORE),
    earliestBookableMonth: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .nullable()
      .default(null)
      .catch(null)
      .describe(DIFF_IGNORE),
    contractEndDate: z.string().date().nullable().default(null).catch(null),
    offboardingReason: OffboardingReasonEnum.nullable()
      .default(null)
      .catch(null),
    offboardingReasonNote: z
      .string()
      .trim()
      .nullable()
      .default(null)
      .catch(null),
    noticePeriod: z.string().trim().nullable().default(null).catch(null),
    cancellationReceivedAt: z
      .string()
      .date()
      .nullable()
      .default(null)
      .catch(null),
    latestBookableMonth: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .nullable()
      .default(null)
      .catch(null),
    automaticPayrollConfirmation: z.boolean(),
    paymentProvisioning: PaymentProvisioningEnum.nullable().default(null),
    provideTrialPayroll: z.boolean(),
    vacationStatistics: z.boolean().nullable(),
    clientNumber: z.number().nullable(),
    name: z.string().trim(),
    accounting: CompanyAccounting.nullable(),
    address: CompanyAddress.nullable(),
    banking: CompanyBanking.nullable(),
    datev: CompanyDatev.nullable(),
    tradeAssociation: CompanyTradeAssociation.nullable(),
    operator: CompanyOperator.nullable(),
    employers: withFieldValue(z.array(z.string())),
    billing: CompanyBilling.nullable(),
    bookingList: CompanyBookingList.nullable(),
    contact: CompanyContact.nullable(),
    managedByTaxAdvisorUid: z.string().nullable(),
    managedByTaxAdvisorName: z.string().nullable(),
    managedByTaxAdvisorMandateType: MandateTypeEnum.nullable(),
    usesTimeTracking: z.boolean(),
    defaultTemporaryAbsenceCompensation: z.boolean().default(true),
    // CL-1590: company-wide default for "Lohnfortzahlung ausgeschlossen". When
    // the default excludes continued pay (false), new contracts inherit this
    // reason as their legal basis (AV/Tarif/Betriebsvereinbarung). Null when
    // continued pay is granted by default.
    defaultTemporaryAbsenceCompensationReason:
      TemporaryAbsenceCompensationReasonEnum.nullable().default(null),
    apps: z.array(z.string()).nullable(),
    nightSurchargeDefaults: z
      .record(z.string(), z.number())
      .nullable()
      .default(null),
    externalIds: z.record(z.string(), z.string()).nullable().default(null),
    maintenanceMode: z.boolean().nullable(),
    stats: CompanyStats.nullable().describe(DIFF_IGNORE),
    updatedAt: z.number(),
    updatedBy: z.string(),
    updatedByName: z.string(),
    updatedByRole: EditorRoleEnum,
    createdAt: z.number(),
    createdBy: z.string(),
    createdByName: z.string(),
    createdByRole: EditorRoleEnum,
  })
  .strict()

export type Company = z.infer<typeof Company>
